'use client';
import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { useEffect, useMemo, useRef } from 'react';

export type ApolloSeedEntry = {
  query: string; // print(DocumentNode)
  variables?: Record<string, any>;
  data: Record<string, any>;
};

function extractRootField(query: string): string | null {
  const m =
    query.match(/query[^{]*\{\s*([A-Za-z0-9_]+)\s*\(/m) ||
    query.match(/query[^{]*\{\s*([A-Za-z0-9_]+)\s*\{/m) ||
    query.match(/\{\s*([A-Za-z0-9_]+)\s*\(/m) ||
    query.match(/\{\s*([A-Za-z0-9_]+)\s*\{/m);
  return m?.[1] ?? null;
}

function normalizePost(p: any) {
  if (!p) return p;
  const out: any = { __typename: p.__typename ?? 'Post', ...p };
  if (out.image) out.image = { __typename: 'File', ...out.image };
  if (out.thumbnail) out.thumbnail = { __typename: 'File', ...out.thumbnail };
  return out;
}

export function ApolloGlobalSeed({ entries }: { entries: ApolloSeedEntry[] }) {
  const client = useApolloClient();
  const seeded = useRef(false);

  const docs = useMemo(
    () =>
      entries.map((e) => ({
        doc: gql`
          ${e.query}
        `,
        e,
      })),
    [entries],
  );

  useEffect(() => {
    if (seeded.current) return;
    seeded.current = true;

    for (const { doc, e } of docs) {
      const root = extractRootField(e.query);
      if (!root) continue;
      const raw = e.data?.[root];
      if (!raw) continue;

      // Build a container type based on the field name
      // paginationPostQuery -> PostPaginationType
      const m = root.match(/^pagination(.+)Query$/i);
      const containerTypename = m ? `${m[1]}PaginationType` : undefined;

      const pageInfo = raw.pageInfo ? { __typename: 'PageInfo', ...raw.pageInfo } : undefined;

      const items = Array.isArray(raw.items) ? raw.items.map(normalizePost) : [];

      const data = {
        [root]: {
          __typename: containerTypename ?? raw.__typename,
          ...raw,
          items,
          ...(pageInfo ? { pageInfo } : {}),
        },
      };

      client.writeQuery({
        query: doc,
        variables: e.variables ?? {},
        data,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
